import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type EmployeeProfile,
  updateEmployeeProfile,
} from '../../../shared/api/employee.service';
import {
  resolveApiLocale,
  resolveAppLanguage,
  type CountryCode,
} from '../../../shared/config';
import { useEmployeeSession } from '../../../shared/hooks/use-employee-session';
import {
  formatTaxIdInput,
  getTaxIdDigits,
  hasValidTaxIdValue,
  isTaxIdFieldError,
  normalizeNamePart,
  resolveEmployeeSessionCountryCode,
  resolveTaxIdErrorMessageKey,
  resolveTaxIdFieldConfig,
  type TaxIdFieldConfig,
} from '../../../shared/model';

export type MyPageSettingsPersonalDataFieldId =
  | 'firstName'
  | 'lastName'
  | 'taxId';
export type MyPageSettingsPersonalDataFieldValues = Record<
  MyPageSettingsPersonalDataFieldId,
  string
>;

type MyPageSettingsPersonalDataForm = {
  canSubmit: boolean;
  countryCode: CountryCode;
  isSubmitting: boolean;
  isTaxIdLocked: boolean;
  isTaxIdInvalid: boolean;
  setFieldValue: (
    fieldId: MyPageSettingsPersonalDataFieldId,
    nextValue: string,
  ) => void;
  submit: () => Promise<boolean>;
  submitError: string | null;
  taxIdErrorMessage: string | null;
  taxIdField: TaxIdFieldConfig;
  values: MyPageSettingsPersonalDataFieldValues;
};

type UseMyPageSettingsPersonalDataFormParams = {
  initialProfile?: EmployeeProfile | null;
};

const EMPLOYEE_QUERY_KEY = ['employee'] as const;

function hasTextValue(value: string): boolean {
  return value.trim().length > 0;
}

function createInitialValues(
  countryCode: CountryCode,
  firstName: string | undefined,
  lastName: string | undefined,
  taxIdDigits: string | undefined,
): MyPageSettingsPersonalDataFieldValues {
  return {
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    taxId: formatTaxIdInput(taxIdDigits ?? '', countryCode),
  };
}

function canSubmitPersonalData(
  values: MyPageSettingsPersonalDataFieldValues,
): boolean {
  return (
    hasTextValue(values.firstName) &&
    hasTextValue(values.lastName) &&
    hasValidTaxIdValue(values.taxId)
  );
}

function resolveTaxIdErrorMessage(
  countryCode: CountryCode,
  error: unknown,
  invalidMessageKey: string,
  taxIdValue: string,
  t: (key: string) => string,
): string | null {
  const messageKey = resolveTaxIdErrorMessageKey(
    countryCode,
    error,
    invalidMessageKey,
    taxIdValue,
  );

  return messageKey ? t(messageKey) : null;
}

function resolveSubmitError(
  error: unknown,
  t: (key: string) => string,
): string | null {
  if (isTaxIdFieldError(error)) {
    return null;
  }

  return error ? t('login.networkError') : null;
}

export function useMyPageSettingsPersonalDataForm({
  initialProfile,
}: UseMyPageSettingsPersonalDataFormParams = {}): MyPageSettingsPersonalDataForm {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { authContact } = useEmployeeSession();
  const profileSave = useMutation({
    mutationFn: updateEmployeeProfile,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: EMPLOYEE_QUERY_KEY,
        refetchType: 'all',
      });
    },
  });
  const profile = initialProfile ?? null;
  const countryCode = resolveEmployeeSessionCountryCode(profile, authContact);
  const isTaxIdLocked = getTaxIdDigits(profile?.tin ?? '').length > 0;
  const [values, setValues] = useState(() =>
    createInitialValues(
      countryCode,
      profile?.firstName,
      profile?.lastName,
      profile?.tin,
    ),
  );
  const taxIdField = resolveTaxIdFieldConfig(countryCode);
  const taxIdErrorMessage = resolveTaxIdErrorMessage(
    countryCode,
    profileSave.error,
    taxIdField.invalidMessageKey,
    values.taxId,
    t,
  );
  const canSubmit = canSubmitPersonalData(values);
  const isTaxIdInvalid = taxIdErrorMessage !== null;

  const setFieldValue = (
    fieldId: MyPageSettingsPersonalDataFieldId,
    nextValue: string,
  ) => {
    if (fieldId === 'taxId' && isTaxIdLocked) {
      return;
    }

    const shouldPreserveTaxIdError =
      fieldId !== 'taxId' && isTaxIdFieldError(profileSave.error);

    if (profileSave.isError && !shouldPreserveTaxIdError) {
      profileSave.reset();
    }

    setValues(current => ({
      ...current,
      [fieldId]:
        fieldId === 'taxId'
          ? formatTaxIdInput(nextValue, countryCode)
          : nextValue,
    }));
  };

  const submit = async () => {
    if (!canSubmit || profileSave.isPending) {
      return false;
    }

    try {
      const payload = {
        firstName: normalizeNamePart(values.firstName),
        lastName: normalizeNamePart(values.lastName),
        locale: resolveApiLocale(resolveAppLanguage(i18n.language)),
        ...(isTaxIdLocked ? {} : { tin: getTaxIdDigits(values.taxId) }),
      };

      await profileSave.mutateAsync(payload);

      return true;
    } catch {
      return false;
    }
  };

  return {
    canSubmit,
    countryCode,
    isSubmitting: profileSave.isPending,
    isTaxIdLocked,
    isTaxIdInvalid,
    setFieldValue,
    submit,
    submitError: resolveSubmitError(profileSave.error, t),
    taxIdErrorMessage,
    taxIdField,
    values,
  };
}
